// Reading the embed's HTML attributes, and resolving them against the
// agent's server-side config.
//
// Every visual and behavioural knob the widget has can be set in two places -
// an HTML attribute on the element, and the agent's config in the console -
// and the attribute always wins. That rule was previously open-coded at five
// separate call sites, each spelling the fallback slightly differently: one
// used `??` on a possibly-empty string, one branched on `!== null`, one
// checked `hasAttribute`, and the text block folded in a fourth level of
// fallback that only two of the five states use. Resolving it in one place
// makes the precedence a thing you can read, and a thing a test can pin.
//
// Everything here is a pure function of (element, config). No DOM writes, no
// fetches, no state.

import type { ServerWidgetConfig } from "./widget-config.js";
import {
  DEFAULT_LISTENING_TEXT,
  DEFAULT_SPEAKING_TEXT,
  DEFAULT_START_CALL_TEXT,
  DEFAULT_THINKING_TEXT,
} from "./styles.js";
import type { SessionOverrides } from "./types.js";

/** Attributes that trigger `attributeChangedCallback`. */
export const OBSERVED_ATTRIBUTES = [
  "agent-id",
  "session-token",
  "session-url",
  "override-prompt",
  "override-first-message",
  "override-voice-id",
  "override-language",
  "dynamic-variables",
  "avatar-image-url",
  "avatar-orb-color-1",
  "avatar-orb-color-2",
  "start-call-text",
  "end-call-text",
  "listening-text",
  "thinking-text",
  "speaking-text",
  "widget-style",
  "widget-theme",
  "show-transcript",
  "speed-control",
  "telemetry",
  // Selects the widget modality: "voice" (default) or "text".
  "mode",
] as const;

/** The per-state button copy, after the full override chain has been applied. */
export interface ResolvedText {
  start: string;
  listening: string;
  thinking: string;
  speaking: string;
}

/** Everything the renderer needs that comes from attributes or config. */
export interface ResolvedAppearance {
  fab: boolean;
  /** Only set when the config supplies one and the host has no explicit attribute. */
  themeFromConfig: "dark" | "light" | "auto" | undefined;
  orbColor1: string | null;
  orbColor2: string | null;
  /** Resolved avatar image, or null to show the mic icon. */
  avatarImageUrl: string | null;
  transcriptEnabled: boolean;
  speedControlEnabled: boolean;
  text: ResolvedText;
}

/** Reads an attribute, treating an absent one as null. */
function attr(el: Element, name: string): string | null {
  return el.getAttribute(name);
}

export function resolveAppearance(el: Element, cfg: ServerWidgetConfig | null): ResolvedAppearance {
  const serverText = cfg?.text;

  // `end-call-text` has no default of its own: it is a convenience for
  // callers who want one "End call" label instead of per-state copy, so it
  // sits between the per-state override and the per-state default for the
  // two states that read as "tap to end". `thinking` deliberately does not
  // consult it - "End call" is a wrong label for a state where nothing has
  // been said yet.
  const endText = attr(el, "end-call-text") ?? serverText?.end_call;

  return {
    fab: (attr(el, "widget-style") ?? cfg?.style) === "fab",
    themeFromConfig: el.hasAttribute("widget-theme") ? undefined : cfg?.theme,
    orbColor1: attr(el, "avatar-orb-color-1") ?? cfg?.avatar?.orb_color_1 ?? null,
    orbColor2: attr(el, "avatar-orb-color-2") ?? cfg?.avatar?.orb_color_2 ?? null,
    avatarImageUrl: resolveAvatarImage(el, cfg),
    transcriptEnabled: resolveTranscript(el, cfg),
    speedControlEnabled: resolveSpeedControl(el),
    text: {
      start: attr(el, "start-call-text") ?? serverText?.start_call ?? DEFAULT_START_CALL_TEXT,
      listening: attr(el, "listening-text") ?? serverText?.listening ?? endText ?? DEFAULT_LISTENING_TEXT,
      thinking: attr(el, "thinking-text") ?? serverText?.thinking ?? DEFAULT_THINKING_TEXT,
      speaking: attr(el, "speaking-text") ?? serverText?.speaking ?? endText ?? DEFAULT_SPEAKING_TEXT,
    },
  };
}

/**
 * The attribute wins outright. Falling back to the config only counts when
 * the server explicitly selected "image": an omitted type, or "orb", means
 * "show the orb" even when an `image_url` happens to be set upstream.
 */
function resolveAvatarImage(el: Element, cfg: ServerWidgetConfig | null): string | null {
  const fromAttr = attr(el, "avatar-image-url");
  if (fromAttr !== null) return fromAttr;
  if (cfg?.avatar?.type !== "image") return null;
  return cfg.avatar.image_url ?? null;
}

/**
 * `show-transcript` is present/absent rather than true/false, except that an
 * explicit "false" turns it off - so an embed can override a config that
 * enabled it.
 */
function resolveSpeedControl(el: Element): boolean {
  // Opt-in, present/absent like `show-transcript`, with an explicit "false"
  // turning it off so a framework that always sets the attribute can disable
  // it. There is no server-side default: a speed control is a decision about
  // the embed's own UI.
  const raw = attr(el, "speed-control");
  return raw !== null && raw !== "false";
}

function resolveTranscript(el: Element, cfg: ServerWidgetConfig | null): boolean {
  const raw = attr(el, "show-transcript");
  if (raw !== null) return raw !== "false";
  return cfg?.transcript?.enabled ?? false;
}

/**
 * Per-call overrides for the session mint. Returns undefined when the embed
 * sets none, so the caller can omit the key entirely rather than sending an
 * empty object.
 */
export function readOverrides(el: Element): SessionOverrides | undefined {
  const out: SessionOverrides = {};
  const prompt = attr(el, "override-prompt");
  const firstMessage = attr(el, "override-first-message");
  const voiceId = attr(el, "override-voice-id");
  const language = attr(el, "override-language");
  if (prompt !== null) out.prompt = prompt;
  if (firstMessage !== null) out.firstMessage = firstMessage;
  if (voiceId !== null) out.voiceId = voiceId;
  if (language !== null) out.language = language;
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Parses the `dynamic-variables` JSON object. Anything that is not an object
 * - a number, an array, unparseable text - is a misuse rather than a value,
 * so it warns and yields undefined instead of sending something the server
 * will reject.
 */
export function readDynamicVariables(
  el: Element,
  warn: (...args: unknown[]) => void,
): Record<string, unknown> | undefined {
  const raw = attr(el, "dynamic-variables");
  if (raw === null || raw.trim() === "") return undefined;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
    warn("<speechify-agent> dynamic-variables must be a JSON object, got:", parsed);
    return undefined;
  } catch (err) {
    warn("<speechify-agent> failed to parse dynamic-variables JSON:", err);
    return undefined;
  }
}

/**
 * Standard HTML boolean attribute: presence enables regardless of value, so
 * `debug="false"` also enables. React's `debug={false}` drops the attribute
 * entirely, so the framework path is unaffected.
 */
export function readBooleanAttribute(el: Element, name: string): boolean {
  return el.hasAttribute(name);
}

/** True when the embedder has switched telemetry off in HTML. */
export function telemetryDisabled(el: Element): boolean {
  const v = attr(el, "telemetry");
  return v === "off" || v === "false";
}
